import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Link as MuiLink,
  Breadcrumbs,
  CssBaseline,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link as RouterLink } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useDispatch, useSelector } from "react-redux";
import { getFooterLinks } from "../../features/user/userSlice";
import Loader from "../../components/common/Loader";

const theme = createTheme({
  typography: {
    fontFamily: "Inter, sans-serif",
  },
  palette: {
    primary: {
      main: "#F4D03F",
      light: "#F8E084",
      dark: "#D4AF37",
      contrastText: "#212121",
      lightest: "#FFFBEB",
    },
    background: {
      default: "#FFFFFF",
      paper: "#ffffff",
    },
    text: {
      primary: "#212121",
      secondary: "#555555",
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: "16px",
          paddingRight: "16px",
          "@media (min-width: 600px)": {
            paddingLeft: "24px",
            paddingRight: "24px",
          },
        },
      },
    },
  },
});

const StaticSitemap = () => {
  const dispatch = useDispatch();
  const { footerLinks, footerLinksLoading, footerLinksError } = useSelector(
    (state) => state.user
  );

  // Detect phone (<= 600px). Using a raw media query avoids depending on Theme context.
  const isPhone = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    dispatch(getFooterLinks());
  }, [dispatch]);

  if (footerLinksLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
          }}
        >
          <Loader />
        </Box>
      </ThemeProvider>
    );
  }

  if (footerLinksError) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="error">
            Failed to load sitemap. Please try again later.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  const staticLinks = footerLinks?.data?.static || {};
  const dynamicLinks = footerLinks?.data?.dynamic || {};

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          p: 2,
        }}
      >
        <Container
          sx={{
            py: { xs: 6, md: 8 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          {/* Breadcrumbs */}
          <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ flexWrap: "wrap" }}
            >
              <MuiLink href="/" underline="hover" color="text.secondary" variant="body2">
                Home
              </MuiLink>
              <Typography color="text.primary" variant="body2">
                Sitemap
              </Typography>
            </Breadcrumbs>
          </Box>

          {/* Title */}
          <Typography
            align="center"
            fontWeight={700}
            sx={{ mb: { xs: 4, md: 6 }, fontSize: { xs: "1.8rem", md: "2.8rem" }, color: "text.primary" }}
          >
            Sitemap
          </Typography>

          {/* PHONE LAYOUT: stacked + centered */}
          {isPhone ? (
            <Box
              component="section"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                px: 1,
              }}
            >
              {Object.entries(staticLinks).map(([section, links], index) => (
                <Box
                  key={`static-${index}`}
                  sx={{
                    width: "100%",
                    maxWidth: 520, // control width on phone so it looks centered & card-like
                    textAlign: "left",
                  }}
                >
                  <Typography fontWeight={700} sx={{ mb: 1.25, color: "primary.dark" }}>
                    {section}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {links.map((link, li) => (
                      <MuiLink key={li} href={link.url} underline="none" sx={{ display: "block" }}>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "primary.lightest",
                            border: "1px solid",
                            borderColor: "primary.light",
                            borderRadius: 1,
                            transition: "background-color 0.3s, border-color 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              bgcolor: "primary.main",
                              borderColor: "primary.dark",
                              boxShadow: 2,
                            },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", wordBreak: "break-word" }}>
                            {link.label}
                          </Typography>
                        </Box>
                      </MuiLink>
                    ))}
                  </Box>
                </Box>
              ))}

              {Object.entries(dynamicLinks).map(([section, links], index) => (
                <Box
                  key={`dynamic-${index}`}
                  sx={{
                    width: "100%",
                    maxWidth: 520,
                    textAlign: "left",
                  }}
                >
                  <Typography fontWeight={700} sx={{ mb: 1.25, color: "primary.dark" }}>
                    {section}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {links.map((link, li) => (
                      <MuiLink
                        key={li}
                        component={RouterLink}
                        to={`/pages/slug/${link.slug}`}
                        underline="none"
                        sx={{ display: "block" }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "primary.lightest",
                            border: "1px solid",
                            borderColor: "primary.light",
                            borderRadius: 1,
                            transition: "background-color 0.3s, border-color 0.3s, box-shadow 0.3s",
                            "&:hover": {
                              bgcolor: "primary.main",
                              borderColor: "primary.dark",
                              boxShadow: 2,
                            },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", wordBreak: "break-word" }}>
                            {link.label}
                          </Typography>
                        </Box>
                      </MuiLink>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* DESKTOP/TABLET GRID LAYOUT */
            <Grid container spacing={{ xs: 3, md: 6 }} justifyContent="flex-start">
              {Object.entries(staticLinks).map(([section, links], colIndex) => (
                <Grid item xs={12} sm={12} md={4} lg={3} key={`static-${colIndex}`}>
                  <Box sx={{ mb: { xs: 3, md: 0 } }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "primary.dark" }}>
                      {section}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {links.map((link, linkIndex) => (
                        <MuiLink key={linkIndex} href={link.url} underline="none" sx={{ display: "block" }}>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: "primary.lightest",
                              border: "1px solid",
                              borderColor: "primary.light",
                              borderRadius: 1,
                              transition: "background-color 0.3s, border-color 0.3s, box-shadow 0.3s",
                              "&:hover": {
                                bgcolor: "primary.main",
                                borderColor: "primary.dark",
                                boxShadow: 2,
                              },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", wordBreak: "break-word" }}>
                              {link.label}
                            </Typography>
                          </Box>
                        </MuiLink>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}

              {Object.entries(dynamicLinks).map(([section, links], colIndex) => (
                <Grid item xs={12} sm={12} md={4} lg={3} key={`dynamic-${colIndex}`}>
                  <Box sx={{ mb: { xs: 3, md: 0 } }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: "primary.dark" }}>
                      {section}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {links.map((link, linkIndex) => (
                        <MuiLink
                          key={linkIndex}
                          component={RouterLink}
                          to={`/pages/slug/${link.slug}`}
                          underline="none"
                          sx={{ display: "block" }}
                        >
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: "primary.lightest",
                              border: "1px solid",
                              borderColor: "primary.light",
                              borderRadius: 1,
                              transition: "background-color 0.3s, border-color 0.3s, box-shadow 0.3s",
                              "&:hover": {
                                bgcolor: "primary.main",
                                borderColor: "primary.dark",
                                boxShadow: 2,
                              },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary", wordBreak: "break-word" }}>
                              {link.label}
                            </Typography>
                          </Box>
                        </MuiLink>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default StaticSitemap;
